/**
 * vehicle/system.ts — Getting There Together
 *
 * Vehicles are mobile furniture. They have no soul, no genome, no needs.
 * They're built, they carry people, they go where you point them.
 * Some need a mount attached (carriages), some are self-powered (bicycles).
 */

import type { Direction } from "../engine/state.js";

// ─── Vehicle Types ──────────────────────────────────────────────────────────

export type VehicleType = "cart" | "carriage" | "boat" | "flying_carpet" | "bicycle";

export interface VehicleTemplate {
  id: string;
  type: VehicleType;
  displayName: string;
  tileFootprint: { width: number; height: number };
  maxPassengers: number;
  speed: number;                   // tiles/sec
  requiresMount: boolean;
  terrainTypes: Set<string>;       // which tile types it can traverse
  customizable: boolean;
  customizationSlots?: string[];
  description: string;
}

// ─── Vehicle State ──────────────────────────────────────────────────────────

export interface VehicleState {
  id: string;
  templateId: string;
  position: { tileX: number; tileY: number; elevation: number };
  direction: Direction;
  passengers: string[];            // player IDs
  driverId: string | null;
  attachedMountId: string | null;  // creature ID of the pulling mount
  customization: Record<string, { style: string; color: string }>;
  moveProgress: number;
  movementQueue: Array<{ tileX: number; tileY: number }>;
}

// ─── Active Vehicle ─────────────────────────────────────────────────────────

export interface ActiveVehicle {
  vehicleId: string;
  role: "driver" | "passenger";
}

// ─── Vehicle Templates ──────────────────────────────────────────────────────

const templates = new Map<string, VehicleTemplate>();

export function registerVehicleTemplate(template: VehicleTemplate): void {
  templates.set(template.id, template);
}

export function getVehicleTemplate(id: string): VehicleTemplate | undefined {
  return templates.get(id);
}

// ─── Vehicle Operations ─────────────────────────────────────────────────────

export function canBoardVehicle(
  vehicle: VehicleState,
  playerDistance: number
): { canBoard: boolean; reason: string } {
  const template = getVehicleTemplate(vehicle.templateId);
  if (!template) return { canBoard: false, reason: "Unknown vehicle type." };

  if (playerDistance > 1) {
    return { canBoard: false, reason: "Too far away to board." };
  }

  if (vehicle.passengers.length >= template.maxPassengers) {
    return { canBoard: false, reason: "No room in the vehicle." };
  }

  if (template.requiresMount && !vehicle.attachedMountId) {
    return { canBoard: false, reason: "This vehicle needs a mount attached to move." };
  }

  return { canBoard: true, reason: "" };
}

export function boardVehicle(
  vehicle: VehicleState,
  playerId: string,
  asDriver: boolean
): ActiveVehicle {
  if (asDriver && !vehicle.driverId) {
    vehicle.driverId = playerId;
  }
  if (!vehicle.passengers.includes(playerId)) {
    vehicle.passengers.push(playerId);
  }

  return {
    vehicleId: vehicle.id,
    role: vehicle.driverId === playerId ? "driver" : "passenger",
  };
}

export function exitVehicle(vehicle: VehicleState, playerId: string): void {
  vehicle.passengers = vehicle.passengers.filter((id) => id !== playerId);
  if (vehicle.driverId === playerId) {
    vehicle.driverId = null;
  }
}

export function attachMountToVehicle(vehicle: VehicleState, creatureId: string): boolean {
  const template = getVehicleTemplate(vehicle.templateId);
  if (!template?.requiresMount) return false;
  vehicle.attachedMountId = creatureId;
  return true;
}

// ─── Vehicle Movement ───────────────────────────────────────────────────────

export function updateVehicle(
  vehicle: VehicleState,
  dt: number
): void {
  if (vehicle.movementQueue.length === 0) return;

  const template = getVehicleTemplate(vehicle.templateId);
  if (!template) return;

  const speed = template.speed;
  vehicle.moveProgress += dt * speed;

  if (vehicle.moveProgress >= 1.0) {
    const target = vehicle.movementQueue[0];
    vehicle.position.tileX = target.tileX;
    vehicle.position.tileY = target.tileY;
    vehicle.moveProgress = 0;
    vehicle.movementQueue.shift();

    // Update direction
    if (vehicle.movementQueue.length > 0) {
      const next = vehicle.movementQueue[0];
      const dx = next.tileX - vehicle.position.tileX;
      const dy = next.tileY - vehicle.position.tileY;
      if (dx > 0 && dy > 0) vehicle.direction = "SE";
      else if (dx > 0 && dy < 0) vehicle.direction = "NE";
      else if (dx < 0 && dy > 0) vehicle.direction = "SW";
      else if (dx < 0 && dy < 0) vehicle.direction = "NW";
      else if (dx > 0) vehicle.direction = "E";
      else if (dx < 0) vehicle.direction = "W";
      else if (dy > 0) vehicle.direction = "S";
      else vehicle.direction = "N";
    }
  }
}

// ─── Vehicle Factory ────────────────────────────────────────────────────────

let nextVehicleId = 1;

export function createVehicle(
  templateId: string,
  position: { tileX: number; tileY: number; elevation: number }
): VehicleState | null {
  const template = getVehicleTemplate(templateId);
  if (!template) return null;

  return {
    id: `vehicle_${nextVehicleId++}`,
    templateId,
    position: { ...position },
    direction: "S",
    passengers: [],
    driverId: null,
    attachedMountId: null,
    customization: {},
    moveProgress: 0,
    movementQueue: [],
  };
}
